import { PlusOutlined } from '@ant-design/icons';
import { ValidatorProvider } from '@dofleini/validator';
import { Button, Form, message } from 'antd';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import React, { memo, useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';

import CNAESelect from '@/modules/configuration/components/CNAESelect';
import ActivitiesListPreview
  from '@/modules/proposalRequest/components/ProposalActivitiesSelect/components/ActivitiesListPreview';

import style from './index.less';

const ProposalActivitiesSelect = ({ value, onChange, isLarge, mainActivity, filters, autoAdd, ...rest }) => {
  const { t } = useTranslation('proposalRequest');
  const [selection, setSelection] = useState();
  const [records, setRecords] = useState({});
  
  const handleAddElement = useCallback((e) => {
    const exist = value.includes(e || selection);
    if (!exist) {
      onChange && onChange([...value, e || selection]);
    } else {
      message.error(t('proposalRequest.form.general.activities.repeatedElement'));
    }
    setSelection(null);
  }, [onChange, selection, t, value]);
  
  const handleSelectionChange = useCallback((e) => {
    setSelection(e);
    if (autoAdd && !!e)
      handleAddElement(e);
  }, [autoAdd, handleAddElement]);
  
  const onLoadData = useCallback((e) => {
    const map = e?.reduce((r, e) => {
      r[e.id] = e;
      return r;
    }, {});
    setRecords(map);
  }, []);
  
  const handleRemove = useCallback((e) => {
    onChange && onChange(value.filter((r) => {
      if (r?.id) {
        return r?.id !== e;
      } else {
        return r !== e;
      }
    }));
  }, [onChange, value]);
  
  const ignoreValues = useMemo(() => [...(value || []), mainActivity], [mainActivity, value]);
  
  return (
    <div className="w-full truncate">
      <div
        className={classNames(style.container, 'flex items-center w-full truncate', { [style.containerWithAutoAdd]: !autoAdd })}>
        <CNAESelect
          {...rest}
          filters={filters}
          ignoreValues={ignoreValues}
          afterLoadData={onLoadData}
          value={selection}
          size={isLarge && 'large'}
          onChange={handleSelectionChange}
          placeholder={t('selectPlaceholder')}
        />
        {!autoAdd && <div>
          <Button
            disabled={!selection}
            type={'primary'}
            size={isLarge && 'large'}
            onClick={() => handleAddElement()}
            className={classNames(style.addButton)}>
            <PlusOutlined/>
          </Button>
        </div>}
      </div>
      <ActivitiesListPreview value={value} records={records} onRemove={handleRemove}/>
    </div>
  );
  
};

ProposalActivitiesSelect.propTypes = {
  mainActivity: PropTypes.any,
  value: PropTypes.array,
  filters: PropTypes.object,
  autoAdd: PropTypes.bool,
  isLarge: PropTypes.bool,
  onChange: PropTypes.func
};

ProposalActivitiesSelect.defaultProps = {
  value: [],
  isLarge: true,
  autoAdd: true,
};

export default memo(ProposalActivitiesSelect);

export const renderProposalActivitiesSelect = ({ formItemProps, field, form }) => {
  
  const rules = field?.required && [{ required: true, message: ValidatorProvider.t('validateMessages.required') }];
  
  return (
    <Form.Item {...formItemProps} required={field?.required} rules={rules}>
      <ProposalActivitiesSelect {...field?.widgetProps} {...field} form={form}/>
    </Form.Item>
  );
};
